"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { CheckBox } from "@/components/ui/CheckBox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { coinGeckoPickerProps } from "@/hooks/useCoinGeckoSearch";
import { resolveChainScope } from "@/lib/templates/resolveChainScope";
import { resolveAllowedChainIdsFromSources, type ActionSchemaLookup, type TemplatePropSource } from "@/lib/templates/resolveChainFilter";
import { isGoogleSheetsConnectorType } from "@/lib/workflow/notificationChannels";
import { useConnectorsStore } from "@/stores/connectorsStore";
import type { TemplateProp } from "@/stores/templatesStore";
import AddressInput from "./AddressInput";
import AssetSelector from "./AssetSelector";
import ContractAddressInput from "./ContractAddressInput";
import ArrayTagInput from "./ArrayTagInput";
import ChainSelector from "./ChainSelector";
import CsvTableInput, { type CsvTableValue } from "./CsvTableInput";
import type { TokenInfo } from "./csvTableCells";
import EmailInput from "./EmailInput";
import PolymarketUserInput from "./PolymarketUserInput";
import RecipientAddressInput from "./RecipientAddressInput";
import RecurrenceInput from "./RecurrenceInput";
import SlackChannelSelector from "./SlackChannelSelector";
import PushbulletDeviceSelector from "./PushbulletDeviceSelector";
import PushoverRecipientSelector from "./PushoverRecipientSelector";
import TelegramChatSelector from "./TelegramChatSelector";
import WebPushSubscriptionSelector from "./WebPushSubscriptionSelector";
import TokenAmountInput from "./TokenAmountInput";
import TokenSelector from "./TokenSelector";
import TwitterUserSelector from "./TwitterUserSelector";
import MorphoVaultSelector from "./MorphoVaultSelector";
import MorphoMarketSelector from "./MorphoMarketSelector";
import GoogleSheetInput from "./GoogleSheetInput";

/** Finds the connector ID for a given type from explicit mapping or user's connectors. */
function findConnectorId(type: string, connectors: Array<{
  id: string;
  type: string;
}>, connectorMapping?: Record<string, string>): string | undefined {
  if (connectorMapping) {
    const mappedId = Object.values(connectorMapping).find(id => {
      const conn = connectors.find(c => c.id === id);
      return conn?.type === type;
    });
    if (mappedId) return mappedId;
  }
  return connectors.find(c => c.type === type)?.id;
}
function findGoogleSheetsConnector(connectors: Array<{
  id: string;
  type: string;
}>, connectorMapping?: Record<string, string>): {
  id: string;
  type: string;
} | undefined {
  if (connectorMapping) {
    for (const id of Object.values(connectorMapping)) {
      const conn = connectors.find(c => c.id === id);
      if (conn && isGoogleSheetsConnectorType(conn.type)) return conn;
    }
  }
  return connectors.find(c => isGoogleSheetsConnectorType(c.type));
}
interface TemplatePropValueInputProps {
  prop: TemplateProp;
  value: any;
  onChange: (value: any) => void;
  /** All current prop values — used for cross-prop references like chainPropKey */
  propValues?: Record<string, any>;
  /** All template props — used to look up sibling prop schemas (e.g., chain prop's enum for token selectors) */
  allProps?: TemplateProp[];
  /** Update a different prop's value (e.g., morphoVault updating its linked chain prop) */
  onPropChange?: (propKey: string, value: any) => void;
  /** Selected connector IDs — used to infer connector context for rich selectors (e.g., telegram-bot) */
  connectorMapping?: Record<string, string>;
  /** Called when the telegram chat selector picks a chat belonging to a specific connector */
  onTelegramConnectorSelect?: (connectorId: string) => void;
  /** Called when the pushover recipient selector picks a recipient belonging to a specific connector */
  onPushoverConnectorSelect?: (connectorId: string) => void;
  /** Called when the pushbullet device selector picks a device belonging to a specific connector */
  onPushbulletConnectorSelect?: (connectorId: string) => void;
  /** Called when the webpush subscription selector picks a subscription belonging to a specific connector */
  onWebPushConnectorSelect?: (connectorId: string) => void;
  /** Only applied to enum selects and text/number inputs; rich selectors have their own styling */
  className?: string;
  /** Workflow wallet address — passed to tokenSelector for showing wallet balances */
  workflowWalletAddress?: string;
  /** Map of workflow nodeId → action type, derived from workflow definition.
   *  Used to resolve chain filter from prop.properties.sources. */
  nodeTypesByNodeId?: Record<string, string>;
  /** Map of actionType → payloadSchema, derived from useActions() in parent.
   *  Used alongside nodeTypesByNodeId to resolve chain filter from sources. */
  actionByType?: ActionSchemaLookup;
  /**
   * Token metadata resolved per csvTable tokenAmount column (keyed by
   * `<propKey>:<colKey>`). Forwarded to `CsvTableInput` so cells can
   * render helper text and the sum aggregate can format with symbol.
   * Owned by `ConnectorInputModal` so a single `useSimTokensInfo` call
   * serves all csvTable props in the template.
   */
  csvTableTokenInfo?: Record<string, TokenInfo | undefined>;
  /** Reports validation state for inputs that have async validation (e.g., Google Sheets URL). */
  onValidationStateChange?: (propKey: string, valid: boolean) => void;
}

/**
 * Renders the appropriate input component for a template prop based on its
 * type and optional inputType hint. Handles rich selectors (chain, token,
 * address, email) as well as basic types (enum, boolean, string, number).
 */
export default function TemplatePropValueInput(t0) {
  const $ = _c(277);
  const {
    prop,
    value,
    onChange,
    propValues,
    allProps,
    onPropChange,
    connectorMapping,
    onTelegramConnectorSelect,
    onPushoverConnectorSelect,
    onPushbulletConnectorSelect,
    onWebPushConnectorSelect,
    className,
    workflowWalletAddress,
    nodeTypesByNodeId,
    actionByType,
    csvTableTokenInfo,
    onValidationStateChange
  } = t0;
  const inputType = prop.properties?.inputType;
  const connectors = useConnectorsStore(_temp);
  let t1;
  if ($[0] !== onChange) {
    t1 = v => onChange(v || undefined);
    $[0] = onChange;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleSheetChange = t1;
  let t2;
  if ($[2] !== onValidationStateChange || $[3] !== prop.key) {
    t2 = valid => onValidationStateChange?.(prop.key, valid);
    $[2] = onValidationStateChange;
    $[3] = prop.key;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleSheetValidation = t2;
  let t3;
  bb0: {
    const chainPropKey = prop.properties?.chainPropKey;
    if (!chainPropKey || !allProps) {
      t3 = undefined;
      break bb0;
    }
    let enumVal;
    let t4;
    if ($[5] !== allProps || $[6] !== chainPropKey) {
      let t5;
      if ($[9] !== chainPropKey) {
        t5 = p => p.key === chainPropKey;
        $[9] = chainPropKey;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      const chainProp = allProps.find(t5);
      enumVal = chainProp?.properties?.enum;
      t4 = Array.isArray(enumVal) && enumVal.every(_temp2);
      $[5] = allProps;
      $[6] = chainPropKey;
      $[7] = enumVal;
      $[8] = t4;
    } else {
      enumVal = $[7];
      t4 = $[8];
    }
    if (t4) {
      t3 = enumVal as number[];
      break bb0;
    }
    t3 = undefined;
  }
  const chainPropEnum = t3;
  if (inputType === "twitterUsername") {
    const selectorValue = Array.isArray(value) ? value as string[] : typeof value === "string" && value ? value : undefined;
    let t4;
    if ($[11] !== onChange || $[12] !== prop.type) {
      t4 = v_1 => {
        if (!v_1 || v_1.length === 0) {
          onChange(undefined);
        } else {
          if (prop.type === "array") {
            onChange(v_1);
          } else {
            if (v_1.length === 1) {
              onChange(v_1[0]);
            } else {
              onChange(v_1);
            }
          }
        }
      };
      $[11] = onChange;
      $[12] = prop.type;
      $[13] = t4;
    } else {
      t4 = $[13];
    }
    let t5;
    if ($[14] !== prop.required || $[15] !== selectorValue || $[16] !== t4) {
      t5 = <TwitterUserSelector label="" value={selectorValue} onChange={t4} required={prop.required} />;
      $[14] = prop.required;
      $[15] = selectorValue;
      $[16] = t4;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    return t5;
  }
  if (inputType === "csvTable") {
    let t4;
    if ($[18] !== prop.properties?.columns) {
      t4 = prop.properties?.columns ?? [];
      $[18] = prop.properties?.columns;
      $[19] = t4;
    } else {
      t4 = $[19];
    }
    const columns = t4;
    let t5;
    if ($[20] !== columns || $[21] !== csvTableTokenInfo || $[22] !== prop.key) {
      t5 = csvTableTokenInfo ? columns.reduce((acc, col) => {
        const info = csvTableTokenInfo[`${prop.key}:${col.key}`];
        if (info) {
          acc[col.key] = info;
        }
        return acc;
      }, {}) : undefined;
      $[20] = columns;
      $[21] = csvTableTokenInfo;
      $[22] = prop.key;
      $[23] = t5;
    } else {
      t5 = $[23];
    }
    const tokenInfoForThisProp = t5;
    const t6 = (value ?? undefined) as CsvTableValue | undefined;
    let t7;
    if ($[24] !== onChange) {
      t7 = v_2 => onChange(v_2);
      $[24] = onChange;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    const t8 = prop.properties?.minRows;
    const t9 = prop.properties?.maxRows;
    const t10 = prop.properties?.summary;
    let t11;
    if ($[26] !== columns || $[27] !== propValues || $[28] !== t10 || $[29] !== t6 || $[30] !== t7 || $[31] !== t8 || $[32] !== t9 || $[33] !== tokenInfoForThisProp) {
      t11 = <CsvTableInput columns={columns} value={t6} onChange={t7} minRows={t8} maxRows={t9} propValues={propValues} summary={t10} tokenInfoByColKey={tokenInfoForThisProp} />;
      $[26] = columns;
      $[27] = propValues;
      $[28] = t10;
      $[29] = t6;
      $[30] = t7;
      $[31] = t8;
      $[32] = t9;
      $[33] = tokenInfoForThisProp;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    return t11;
  }
  if (inputType === "chainSelector") {
    const explicitEnum = Array.isArray(prop.properties?.enum) && prop.properties.enum.every(_temp3) ? prop.properties.enum as number[] : undefined;
    const sources = prop.properties?.sources as TemplatePropSource[] | undefined;
    let t4;
    if ($[35] !== actionByType || $[36] !== explicitEnum || $[37] !== nodeTypesByNodeId || $[38] !== sources) {
      t4 = explicitEnum !== undefined || !nodeTypesByNodeId || !actionByType ? undefined : resolveAllowedChainIdsFromSources(sources, actionByType, nodeTypesByNodeId);
      $[35] = actionByType;
      $[36] = explicitEnum;
      $[37] = nodeTypesByNodeId;
      $[38] = sources;
      $[39] = t4;
    } else {
      t4 = $[39];
    }
    const derivedFromSources = t4;
    const allowedChainIds = explicitEnum ?? derivedFromSources;
    let t5;
    if ($[40] !== allowedChainIds || $[41] !== onChange || $[42] !== value) {
      t5 = <ChainSelector label="" value={value} onChange={onChange} popoverModal={true} allowedChainIds={allowedChainIds} />;
      $[40] = allowedChainIds;
      $[41] = onChange;
      $[42] = value;
      $[43] = t5;
    } else {
      t5 = $[43];
    }
    return t5;
  }
  if (inputType === "morphoVault") {
    const chainPropKey_0 = prop.properties?.chainPropKey;
    const chainId = chainPropKey_0 && propValues ? propValues[chainPropKey_0] : undefined;
    const t4 = value ?? "";
    let t5;
    if ($[44] !== onChange) {
      t5 = v_4 => onChange(v_4 || undefined);
      $[44] = onChange;
      $[45] = t5;
    } else {
      t5 = $[45];
    }
    const t6 = chainId != null ? Number(chainId) : undefined;
    let t7;
    if ($[46] !== chainPropKey_0 || $[47] !== onPropChange) {
      t7 = chainPropKey_0 && onPropChange ? newChainId => onPropChange(chainPropKey_0, newChainId) : undefined;
      $[46] = chainPropKey_0;
      $[47] = onPropChange;
      $[48] = t7;
    } else {
      t7 = $[48];
    }
    let t8;
    if ($[49] !== t4 || $[50] !== t5 || $[51] !== t6 || $[52] !== t7) {
      t8 = <MorphoVaultSelector label="" value={t4} onChange={t5} chainId={t6} popoverModal={true} showChainFilter={true} onChainIdChange={t7} />;
      $[49] = t4;
      $[50] = t5;
      $[51] = t6;
      $[52] = t7;
      $[53] = t8;
    } else {
      t8 = $[53];
    }
    return t8;
  }
  if (inputType === "morphoMarket") {
    const chainId_0 = prop.properties?.chainPropKey && propValues ? propValues[prop.properties.chainPropKey] : undefined;
    const loanAssetAddress = prop.properties?.loanAssetAddress as string | undefined;
    const t4 = value ?? "";
    let t5;
    if ($[54] !== onChange) {
      t5 = v_5 => onChange(v_5 || undefined);
      $[54] = onChange;
      $[55] = t5;
    } else {
      t5 = $[55];
    }
    const t6 = chainId_0 != null ? Number(chainId_0) : prop.properties?.chainId != null ? Number(prop.properties.chainId) : undefined;
    let t7;
    if ($[56] !== loanAssetAddress || $[57] !== t4 || $[58] !== t5 || $[59] !== t6) {
      t7 = <MorphoMarketSelector label="" value={t4} onChange={t5} chainId={t6} loanAssetAddress={loanAssetAddress} popoverModal={true} />;
      $[56] = loanAssetAddress;
      $[57] = t4;
      $[58] = t5;
      $[59] = t6;
      $[60] = t7;
    } else {
      t7 = $[60];
    }
    return t7;
  }
  if (inputType === "storkAsset") {
    const t4 = value ?? "";
    let t5;
    if ($[61] !== onChange) {
      t5 = v_6 => onChange(v_6 || undefined);
      $[61] = onChange;
      $[62] = t5;
    } else {
      t5 = $[62];
    }
    let t6;
    if ($[63] !== prop.required || $[64] !== t4 || $[65] !== t5) {
      t6 = <AssetSelector label="" value={t4} onChange={t5} required={prop.required} popoverModal={true} />;
      $[63] = prop.required;
      $[64] = t4;
      $[65] = t5;
      $[66] = t6;
    } else {
      t6 = $[66];
    }
    return t6;
  }
  if (inputType === "tokenSelector") {
    let t4;
    if ($[67] !== prop.properties || $[68] !== propValues) {
      t4 = resolveChainScope(prop.properties, propValues);
      $[67] = prop.properties;
      $[68] = propValues;
      $[69] = t4;
    } else {
      t4 = $[69];
    }
    const {
      chainId: chainId_1,
      chainIds,
      showChainFilter
    } = t4;
    const t5 = value ?? "";
    let t6;
    if ($[70] !== onChange) {
      t6 = v_7 => {
        const raw = typeof v_7 === "string" ? v_7 : v_7[0] ?? "";
        onChange(raw || undefined);
      };
      $[70] = onChange;
      $[71] = t6;
    } else {
      t6 = $[71];
    }
    const t7 = prop.properties?.excludeNative;
    let t8;
    if ($[72] !== chainId_1 || $[73] !== chainIds || $[74] !== chainPropEnum || $[75] !== prop.required || $[76] !== showChainFilter || $[77] !== t5 || $[78] !== t6 || $[79] !== t7 || $[80] !== workflowWalletAddress) {
      t8 = <TokenSelector label="" value={t5} onChange={t6} chainId={chainId_1} chainIds={chainIds} showChainFilter={showChainFilter} required={prop.required} popoverModal={true} workflowWalletAddress={workflowWalletAddress} allowedChainIds={chainPropEnum} excludeNative={t7} />;
      $[72] = chainId_1;
      $[73] = chainIds;
      $[74] = chainPropEnum;
      $[75] = prop.required;
      $[76] = showChainFilter;
      $[77] = t5;
      $[78] = t6;
      $[79] = t7;
      $[80] = workflowWalletAddress;
      $[81] = t8;
    } else {
      t8 = $[81];
    }
    return t8;
  }
  if (inputType === "coinSelector") {
    const t4 = value ?? "";
    let t5;
    if ($[82] !== onChange) {
      t5 = v_8 => {
        const raw_0 = typeof v_8 === "string" ? v_8 : v_8[0] ?? "";
        onChange(raw_0 || undefined);
      };
      $[82] = onChange;
      $[83] = t5;
    } else {
      t5 = $[83];
    }
    let t6;
    if ($[84] !== prop.required || $[85] !== t4 || $[86] !== t5) {
      t6 = <TokenSelector {...coinGeckoPickerProps} label="" value={t4} onChange={t5} required={prop.required} popoverModal={true} />;
      $[84] = prop.required;
      $[85] = t4;
      $[86] = t5;
      $[87] = t6;
    } else {
      t6 = $[87];
    }
    return t6;
  }
  if (inputType === "multiCoinSelector") {
    let t4;
    if ($[88] !== value) {
      t4 = Array.isArray(value) ? value : value ? [value] : [];
      $[88] = value;
      $[89] = t4;
    } else {
      t4 = $[89];
    }
    let t5;
    if ($[90] !== onChange) {
      t5 = v_9 => onChange(Array.isArray(v_9) ? v_9 : v_9 ? [v_9] : undefined);
      $[90] = onChange;
      $[91] = t5;
    } else {
      t5 = $[91];
    }
    let t6;
    if ($[92] !== prop.required || $[93] !== t4 || $[94] !== t5) {
      t6 = <TokenSelector {...coinGeckoPickerProps} label="" value={t4} onChange={t5} required={prop.required} popoverModal={true} multiple={true} />;
      $[92] = prop.required;
      $[93] = t4;
      $[94] = t5;
      $[95] = t6;
    } else {
      t6 = $[95];
    }
    return t6;
  }
  if (inputType === "multiTokenSelector") {
    let t4;
    if ($[96] !== prop.properties || $[97] !== propValues) {
      t4 = resolveChainScope(prop.properties, propValues);
      $[96] = prop.properties;
      $[97] = propValues;
      $[98] = t4;
    } else {
      t4 = $[98];
    }
    const {
      chainId: chainId_2,
      chainIds: chainIds_0,
      showChainFilter: showChainFilter_0
    } = t4;
    const t5 = prop.properties?.showWalletBalances ? workflowWalletAddress : undefined;
    const t6 = prop.properties?.excludeNative;
    let t7;
    if ($[99] !== chainId_2 || $[100] !== chainIds_0 || $[101] !== chainPropEnum || $[102] !== onChange || $[103] !== prop.required || $[104] !== showChainFilter_0 || $[105] !== t5 || $[106] !== t6 || $[107] !== value) {
      t7 = <TokenSelector label="" value={value} onChange={onChange} chainId={chainId_2} chainIds={chainIds_0} showChainFilter={showChainFilter_0} required={prop.required} popoverModal={true} multiple={true} workflowWalletAddress={t5} allowedChainIds={chainPropEnum} excludeNative={t6} />;
      $[99] = chainId_2;
      $[100] = chainIds_0;
      $[101] = chainPropEnum;
      $[102] = onChange;
      $[103] = prop.required;
      $[104] = showChainFilter_0;
      $[105] = t5;
      $[106] = t6;
      $[107] = value;
      $[108] = t7;
    } else {
      t7 = $[108];
    }
    return t7;
  }
  if (inputType === "tokenAmount") {
    const chainId_3 = (prop.properties?.chainPropKey && propValues ? propValues[prop.properties.chainPropKey] : undefined) ?? prop.properties?.chainId;
    const tokenAddressRaw = (prop.properties?.tokenAddressPropKey && propValues ? propValues[prop.properties.tokenAddressPropKey] : undefined) ?? prop.properties?.tokenAddress;
    const tokenAddress = typeof tokenAddressRaw === "string" ? tokenAddressRaw : undefined;
    const rawTokenResolve = prop.properties?.tokenResolve;
    const tokenResolve = typeof rawTokenResolve === "string" && ["erc4626", "erc4626-vault"].includes(rawTokenResolve) ? rawTokenResolve as "erc4626" | "erc4626-vault" : undefined;
    const t4 = value != null ? String(value) : "";
    let t5;
    if ($[109] !== onChange) {
      t5 = v_10 => onChange(v_10 || undefined);
      $[109] = onChange;
      $[110] = t5;
    } else {
      t5 = $[110];
    }
    const t6 = chainId_3 != null ? Number(chainId_3) : undefined;
    let t7;
    if ($[111] !== prop.required || $[112] !== t4 || $[113] !== t5 || $[114] !== t6 || $[115] !== tokenAddress || $[116] !== tokenResolve || $[117] !== workflowWalletAddress) {
      t7 = <TokenAmountInput label="" value={t4} onChange={t5} required={prop.required} tokenAddress={tokenAddress} chainId={t6} tokenResolve={tokenResolve} placeholder="Enter amount" workflowWalletAddress={workflowWalletAddress} />;
      $[111] = prop.required;
      $[112] = t4;
      $[113] = t5;
      $[114] = t6;
      $[115] = tokenAddress;
      $[116] = tokenResolve;
      $[117] = workflowWalletAddress;
      $[118] = t7;
    } else {
      t7 = $[118];
    }
    return t7;
  }
  if (inputType === "contractAddress" || inputType === "address" && /contract\s*address/i.test(prop.key ?? "")) {
    let resolvedChainId;
    if (prop.properties?.chainPropKey && propValues) {
      resolvedChainId = propValues[prop.properties.chainPropKey] as number | undefined;
    }
    if (resolvedChainId == null && propValues && allProps) {
      const chainProp_0 = allProps.find(_temp4);
      if (chainProp_0) {
        resolvedChainId = propValues[chainProp_0.key] as number | undefined;
      }
    }
    const t4 = value ?? "";
    let t5;
    if ($[119] !== onChange) {
      t5 = v_11 => onChange(v_11 || undefined);
      $[119] = onChange;
      $[120] = t5;
    } else {
      t5 = $[120];
    }
    const t6 = resolvedChainId != null ? Number(resolvedChainId) : undefined;
    let t7;
    if ($[121] !== prop.required || $[122] !== t4 || $[123] !== t5 || $[124] !== t6) {
      t7 = <ContractAddressInput label="" value={t4} onChange={t5} required={prop.required} chainId={t6} popoverModal={true} />;
      $[121] = prop.required;
      $[122] = t4;
      $[123] = t5;
      $[124] = t6;
      $[125] = t7;
    } else {
      t7 = $[125];
    }
    return t7;
  }
  if (inputType === "address") {
    const allowWalletPicker = prop.properties?.allowWalletPicker !== false;
    const t4 = value ?? "";
    let t5;
    if ($[126] !== onChange) {
      t5 = v_12 => onChange(v_12 || undefined);
      $[126] = onChange;
      $[127] = t5;
    } else {
      t5 = $[127];
    }
    let t6;
    if ($[128] !== allowWalletPicker || $[129] !== prop.required || $[130] !== t4 || $[131] !== t5) {
      t6 = <AddressInput label="" value={t4} onChange={t5} required={prop.required} popoverModal={true} allowWalletPicker={allowWalletPicker} />;
      $[128] = allowWalletPicker;
      $[129] = prop.required;
      $[130] = t4;
      $[131] = t5;
      $[132] = t6;
    } else {
      t6 = $[132];
    }
    return t6;
  }
  if (inputType === "recipientAddress") {
    const t4 = value ?? "";
    let t5;
    if ($[133] !== onChange) {
      t5 = v_13 => onChange(v_13 || undefined);
      $[133] = onChange;
      $[134] = t5;
    } else {
      t5 = $[134];
    }
    let t6;
    if ($[135] !== prop.required || $[136] !== t4 || $[137] !== t5) {
      t6 = <RecipientAddressInput label="" value={t4} onChange={t5} required={prop.required} />;
      $[135] = prop.required;
      $[136] = t4;
      $[137] = t5;
      $[138] = t6;
    } else {
      t6 = $[138];
    }
    return t6;
  }
  if (inputType === "polymarketUser") {
    const t4 = value ?? "";
    let t5;
    if ($[139] !== onChange) {
      t5 = v_14 => onChange(v_14 || undefined);
      $[139] = onChange;
      $[140] = t5;
    } else {
      t5 = $[140];
    }
    let t6;
    if ($[141] !== prop.required || $[142] !== t4 || $[143] !== t5) {
      t6 = <PolymarketUserInput label="" value={t4} onChange={t5} required={prop.required} />;
      $[141] = prop.required;
      $[142] = t4;
      $[143] = t5;
      $[144] = t6;
    } else {
      t6 = $[144];
    }
    return t6;
  }
  if (inputType === "email") {
    const t4 = value ?? "";
    let t5;
    if ($[145] !== onChange) {
      t5 = v_15 => onChange(v_15 || undefined);
      $[145] = onChange;
      $[146] = t5;
    } else {
      t5 = $[146];
    }
    let t6;
    if ($[147] !== prop.required || $[148] !== t4 || $[149] !== t5) {
      t6 = <EmailInput label="" value={t4} onChange={t5} required={prop.required} />;
      $[147] = prop.required;
      $[148] = t4;
      $[149] = t5;
      $[150] = t6;
    } else {
      t6 = $[150];
    }
    return t6;
  }
  if (inputType === "slack-channel") {
    const t4 = value ?? "";
    let t5;
    if ($[151] !== onChange) {
      t5 = v_16 => onChange(Array.isArray(v_16) ? v_16[0] || undefined : v_16 || undefined);
      $[151] = onChange;
      $[152] = t5;
    } else {
      t5 = $[152];
    }
    const t6 = prop.required;
    let t7;
    if ($[153] !== connectorMapping || $[154] !== connectors) {
      t7 = findConnectorId("slack", connectors, connectorMapping);
      $[153] = connectorMapping;
      $[154] = connectors;
      $[155] = t7;
    } else {
      t7 = $[155];
    }
    let t8;
    if ($[156] !== prop.required || $[157] !== t4 || $[158] !== t5 || $[159] !== t7) {
      t8 = <SlackChannelSelector label="" value={t4} onChange={t5} required={t6} connectorId={t7} connectorType="slack" />;
      $[156] = prop.required;
      $[157] = t4;
      $[158] = t5;
      $[159] = t7;
      $[160] = t8;
    } else {
      t8 = $[160];
    }
    return t8;
  }
  if (inputType === "telegram-chat") {
    const t4 = value ?? "";
    let t5;
    if ($[161] !== onChange) {
      t5 = v_17 => onChange(v_17 || undefined);
      $[161] = onChange;
      $[162] = t5;
    } else {
      t5 = $[162];
    }
    const t6 = prop.required;
    let t7;
    if ($[163] !== connectorMapping || $[164] !== connectors) {
      t7 = findConnectorId("telegram-bot", connectors, connectorMapping);
      $[163] = connectorMapping;
      $[164] = connectors;
      $[165] = t7;
    } else {
      t7 = $[165];
    }
    let t8;
    if ($[166] !== onTelegramConnectorSelect || $[167] !== prop.required || $[168] !== t4 || $[169] !== t5 || $[170] !== t7) {
      t8 = <TelegramChatSelector label="" value={t4} onChange={t5} required={t6} connectorId={t7} onConnectorSelect={onTelegramConnectorSelect} />;
      $[166] = onTelegramConnectorSelect;
      $[167] = prop.required;
      $[168] = t4;
      $[169] = t5;
      $[170] = t7;
      $[171] = t8;
    } else {
      t8 = $[171];
    }
    return t8;
  }
  if (inputType === "pushover-recipient") {
    const t4 = value ?? "";
    let t5;
    if ($[172] !== onChange) {
      t5 = v_18 => onChange(v_18 || undefined);
      $[172] = onChange;
      $[173] = t5;
    } else {
      t5 = $[173];
    }
    const t6 = prop.required;
    let t7;
    if ($[174] !== connectorMapping || $[175] !== connectors) {
      t7 = findConnectorId("pushover", connectors, connectorMapping);
      $[174] = connectorMapping;
      $[175] = connectors;
      $[176] = t7;
    } else {
      t7 = $[176];
    }
    let t8;
    if ($[177] !== onPushoverConnectorSelect || $[178] !== prop.required || $[179] !== t4 || $[180] !== t5 || $[181] !== t7) {
      t8 = <PushoverRecipientSelector label="" value={t4} onChange={t5} required={t6} connectorId={t7} onConnectorSelect={onPushoverConnectorSelect} />;
      $[177] = onPushoverConnectorSelect;
      $[178] = prop.required;
      $[179] = t4;
      $[180] = t5;
      $[181] = t7;
      $[182] = t8;
    } else {
      t8 = $[182];
    }
    return t8;
  }
  if (inputType === "pushbullet-device") {
    const t4 = value ?? "";
    let t5;
    if ($[183] !== onChange) {
      t5 = v_19 => onChange(v_19 || undefined);
      $[183] = onChange;
      $[184] = t5;
    } else {
      t5 = $[184];
    }
    const t6 = prop.required;
    let t7;
    if ($[185] !== connectorMapping || $[186] !== connectors) {
      t7 = findConnectorId("pushbullet", connectors, connectorMapping);
      $[185] = connectorMapping;
      $[186] = connectors;
      $[187] = t7;
    } else {
      t7 = $[187];
    }
    let t8;
    if ($[188] !== onPushbulletConnectorSelect || $[189] !== prop.required || $[190] !== t4 || $[191] !== t5 || $[192] !== t7) {
      t8 = <PushbulletDeviceSelector label="" value={t4} onChange={t5} required={t6} connectorId={t7} onConnectorSelect={onPushbulletConnectorSelect} />;
      $[188] = onPushbulletConnectorSelect;
      $[189] = prop.required;
      $[190] = t4;
      $[191] = t5;
      $[192] = t7;
      $[193] = t8;
    } else {
      t8 = $[193];
    }
    return t8;
  }
  if (inputType === "webpush-subscription") {
    const t4 = value ?? "";
    let t5;
    if ($[194] !== onChange) {
      t5 = v_20 => onChange(v_20 || undefined);
      $[194] = onChange;
      $[195] = t5;
    } else {
      t5 = $[195];
    }
    const t6 = prop.required;
    let t7;
    if ($[196] !== connectorMapping || $[197] !== connectors) {
      t7 = findConnectorId("webpush", connectors, connectorMapping);
      $[196] = connectorMapping;
      $[197] = connectors;
      $[198] = t7;
    } else {
      t7 = $[198];
    }
    let t8;
    if ($[199] !== onWebPushConnectorSelect || $[200] !== prop.required || $[201] !== t4 || $[202] !== t5 || $[203] !== t7) {
      t8 = <WebPushSubscriptionSelector label="" value={t4} onChange={t5} required={t6} connectorId={t7} onConnectorSelect={onWebPushConnectorSelect} />;
      $[199] = onWebPushConnectorSelect;
      $[200] = prop.required;
      $[201] = t4;
      $[202] = t5;
      $[203] = t7;
      $[204] = t8;
    } else {
      t8 = $[204];
    }
    return t8;
  }
  if (inputType === "googleSheetUrl") {
    let t4;
    if ($[205] !== connectorMapping || $[206] !== connectors) {
      t4 = findGoogleSheetsConnector(connectors, connectorMapping);
      $[205] = connectorMapping;
      $[206] = connectors;
      $[207] = t4;
    } else {
      t4 = $[207];
    }
    const gsConnector = t4;
    const t5 = value ?? "";
    const t6 = gsConnector?.id;
    const t7 = gsConnector?.type;
    const t8 = onValidationStateChange ? handleSheetValidation : undefined;
    let t9;
    if ($[208] !== handleSheetChange || $[209] !== prop.required || $[210] !== t5 || $[211] !== t6 || $[212] !== t7 || $[213] !== t8) {
      t9 = <GoogleSheetInput label="" value={t5} onChange={handleSheetChange} required={prop.required} connectorId={t6} connectorType={t7} onValidationStateChange={t8} />;
      $[208] = handleSheetChange;
      $[209] = prop.required;
      $[210] = t5;
      $[211] = t6;
      $[212] = t7;
      $[213] = t8;
      $[214] = t9;
    } else {
      t9 = $[214];
    }
    return t9;
  }
  if (inputType === "rrule") {
    const t4 = value ?? "";
    let t5;
    if ($[215] !== onChange) {
      t5 = v_21 => onChange(v_21 || undefined);
      $[215] = onChange;
      $[216] = t5;
    } else {
      t5 = $[216];
    }
    let t6;
    if ($[217] !== t4 || $[218] !== t5) {
      t6 = <RecurrenceInput label="" value={t4} onChange={t5} />;
      $[217] = t4;
      $[218] = t5;
      $[219] = t6;
    } else {
      t6 = $[219];
    }
    return t6;
  }
  if (inputType === "textarea") {
    const t4 = value ?? "";
    let t5;
    if ($[220] !== onChange) {
      t5 = e => onChange(e.target.value || undefined);
      $[220] = onChange;
      $[221] = t5;
    } else {
      t5 = $[221];
    }
    const t6 = prop.properties?.placeholder || `Enter ${prop.name || prop.key}`;
    const t7 = className ?? "w-full rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20";
    let t8;
    if ($[222] !== t4 || $[223] !== t5 || $[224] !== t6 || $[225] !== t7) {
      t8 = <Textarea value={t4} onChange={t5} placeholder={t6} rows={4} className={t7} />;
      $[222] = t4;
      $[223] = t5;
      $[224] = t6;
      $[225] = t7;
      $[226] = t8;
    } else {
      t8 = $[226];
    }
    return t8;
  }
  if (inputType === "password") {
    const t4 = value ?? "";
    let t5;
    if ($[227] !== onChange) {
      t5 = e_0 => onChange(e_0.target.value || undefined);
      $[227] = onChange;
      $[228] = t5;
    } else {
      t5 = $[228];
    }
    const t6 = prop.properties?.placeholder || `Enter ${prop.name || prop.key}`;
    const t7 = className ?? "text-ds-text-primary";
    let t8;
    if ($[229] !== t4 || $[230] !== t5 || $[231] !== t6 || $[232] !== t7) {
      t8 = <Input type="password" value={t4} onChange={t5} placeholder={t6} className={t7} />;
      $[229] = t4;
      $[230] = t5;
      $[231] = t6;
      $[232] = t7;
      $[233] = t8;
    } else {
      t8 = $[233];
    }
    return t8;
  }
  if (prop.properties?.enum) {
    const isExchangeEnum = prop.properties?.format === "exchange";
    const t4 = value != null ? String(value) : "__empty__";
    let t5;
    if ($[234] !== onChange || $[235] !== prop.type) {
      t5 = v_22 => {
        if (v_22 === "__empty__") {
          onChange(undefined);
        } else {
          if (prop.type === "number" || prop.type === "integer") {
            onChange(Number(v_22));
          } else {
            onChange(v_22);
          }
        }
      };
      $[234] = onChange;
      $[235] = prop.type;
      $[236] = t5;
    } else {
      t5 = $[236];
    }
    const t6 = className ?? "border-ds-border-default text-ds-text-primary bg-ds-surface-card h-10 w-full rounded-lg border text-sm";
    let t7;
    if ($[237] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <SelectValue placeholder="Select..." />;
      $[237] = t7;
    } else {
      t7 = $[237];
    }
    let t8;
    if ($[238] !== t6) {
      t8 = <SelectTrigger className={t6}>{t7}</SelectTrigger>;
      $[238] = t6;
      $[239] = t8;
    } else {
      t8 = $[239];
    }
    let t9;
    if ($[240] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <SelectItem value="__empty__">Select...</SelectItem>;
      $[240] = t9;
    } else {
      t9 = $[240];
    }
    let t10;
    if ($[241] !== isExchangeEnum || $[242] !== prop.properties.enum || $[243] !== prop.properties.enumLabels) {
      let t11;
      if ($[245] !== isExchangeEnum || $[246] !== prop.properties.enumLabels) {
        t11 = (val, i) => {
          const label = prop.properties?.enumLabels?.[i] ?? String(val);
          return <SelectItem key={i} value={String(val)}>{isExchangeEnum ? <span className="flex items-center gap-2"><img src={`https://ai-icon.b3.fun/logo/${val}`} alt="" className="border-ds-border-default h-5 w-5 rounded-full border bg-white object-cover" onError={_temp5} />{label}</span> : label}</SelectItem>;
        };
        $[245] = isExchangeEnum;
        $[246] = prop.properties.enumLabels;
        $[247] = t11;
      } else {
        t11 = $[247];
      }
      t10 = prop.properties.enum.map(t11);
      $[241] = isExchangeEnum;
      $[242] = prop.properties.enum;
      $[243] = prop.properties.enumLabels;
      $[244] = t10;
    } else {
      t10 = $[244];
    }
    let t11;
    if ($[248] !== t10) {
      t11 = <SelectContent>{t9}{t10}</SelectContent>;
      $[248] = t10;
      $[249] = t11;
    } else {
      t11 = $[249];
    }
    let t12;
    if ($[250] !== t11 || $[251] !== t4 || $[252] !== t5 || $[253] !== t8) {
      t12 = <Select value={t4} onValueChange={t5}>{t8}{t11}</Select>;
      $[250] = t11;
      $[251] = t4;
      $[252] = t5;
      $[253] = t8;
      $[254] = t12;
    } else {
      t12 = $[254];
    }
    return t12;
  }
  if (prop.type === "boolean") {
    const t4 = !!value;
    let t5;
    if ($[255] !== onChange) {
      t5 = checked => onChange(checked);
      $[255] = onChange;
      $[256] = t5;
    } else {
      t5 = $[256];
    }
    const t6 = value ? "True" : "False";
    let t7;
    if ($[257] !== t4 || $[258] !== t5 || $[259] !== t6) {
      t7 = <CheckBox checked={t4} onChange={t5} label={t6} size="sm" />;
      $[257] = t4;
      $[258] = t5;
      $[259] = t6;
      $[260] = t7;
    } else {
      t7 = $[260];
    }
    return t7;
  }
  if (prop.type === "array") {
    const t4 = prop.properties?.placeholder;
    let t5;
    if ($[261] !== className || $[262] !== onChange || $[263] !== t4 || $[264] !== value) {
      t5 = <ArrayTagInput value={value} onChange={onChange} placeholder={t4} className={className} />;
      $[261] = className;
      $[262] = onChange;
      $[263] = t4;
      $[264] = value;
      $[265] = t5;
    } else {
      t5 = $[265];
    }
    return t5;
  }
  const t4 = prop.type === "number" || prop.type === "integer" ? "number" : "text";
  const t5 = value ?? "";
  let t6;
  if ($[266] !== onChange || $[267] !== prop.type) {
    t6 = e_2 => {
      let newVal = e_2.target.value;
      if (newVal === "") {
        newVal = undefined;
      } else {
        if (prop.type === "number" || prop.type === "integer") {
          newVal = Number(newVal);
          if (isNaN(newVal)) {
            newVal = undefined;
          }
        }
      }
      onChange(newVal);
    };
    $[266] = onChange;
    $[267] = prop.type;
    $[268] = t6;
  } else {
    t6 = $[268];
  }
  const t7 = prop.properties?.placeholder || `Enter ${prop.name || prop.key}`;
  const t8 = prop.properties?.minimum;
  const t9 = prop.properties?.maximum;
  const t10 = className ?? "text-ds-text-primary";
  let t11;
  if ($[269] !== t10 || $[270] !== t4 || $[271] !== t5 || $[272] !== t6 || $[273] !== t7 || $[274] !== t8 || $[275] !== t9) {
    t11 = <Input type={t4} value={t5} onChange={t6} placeholder={t7} min={t8} max={t9} className={t10} />;
    $[269] = t10;
    $[270] = t4;
    $[271] = t5;
    $[272] = t6;
    $[273] = t7;
    $[274] = t8;
    $[275] = t9;
    $[276] = t11;
  } else {
    t11 = $[276];
  }
  return t11;
}
function _temp5(e_1) {
  (e_1.target as HTMLImageElement).style.display = "none";
}
function _temp4(p_0) {
  return p_0.properties?.inputType === "chainSelector";
}
function _temp3(v_3) {
  return typeof v_3 === "number";
}
function _temp2(v_0) {
  return typeof v_0 === "number";
}
function _temp(state) {
  return state.connectors;
}